// Users list page — table with status filter, search, and role count.
// Route: /t/:slug/admin/users

import { useState, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { usersApi } from '@/lib/api/usersRoles'
import type { User } from '@/lib/api/usersRoles'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from '@/lib/hooks/use-toast'
import { UserPlus, Search } from 'lucide-react'
import { useDebounce } from '@/lib/hooks/use-debounce'

const statusOptions = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'suspended', label: 'Suspended' },
] as const

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-800 border-green-200',
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  suspended: 'bg-red-100 text-red-800 border-red-200',
}

export function UsersPage() {
  const { slug } = useTenant()
  const api = useApi()
  const navigate = useNavigate()
  const { toast } = useToast()
  const usersClient = usersApi(api)

  const [statusFilter, setStatusFilter] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebounce(searchInput, 300)

  const filters = useMemo(
    () => ({
      status: statusFilter || undefined,
      search: debouncedSearch || undefined,
    }),
    [statusFilter, debouncedSearch],
  )

  const { data: users, error, isLoading, refetch } = useQuery({
    queryKey: queryKeys.users.list(slug, filters),
    queryFn: () => usersClient.list(filters),
  })

  function handleRowClick(user: User) {
    navigate({ to: `/t/${slug}/admin/users/${user.id}` })
  }

  function handleInviteClick() {
    toast({
      title: 'Coming soon',
      description: 'User invitations will be available in a future update.',
    })
  }

  if (isLoading) return <TableSkeleton rows={8} />

  if (error) {
    return (
      <ErrorState
        message="Failed to load users"
        technicalDetail={error.message}
        onRetry={() => refetch()}
      />
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Users</h1>
          <p className="text-sm text-muted-foreground">
            Manage user accounts and access.
          </p>
        </div>
        <Button onClick={handleInviteClick}>
          <UserPlus className="mr-2 h-4 w-4" />
          Invite User
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 w-48 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      {users && users.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">
          {debouncedSearch || statusFilter ? 'No users match the current filters.' : 'No users found.'}
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium">Name / Email</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Roles</th>
                <th className="px-4 py-3 text-left font-medium">Last Login</th>
                <th className="px-4 py-3 text-left font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {users?.map((user) => (
                <tr
                  key={user.id}
                  onClick={() => handleRowClick(user)}
                  className="cursor-pointer border-b transition-colors hover:bg-muted/50 last:border-0"
                >
                  <td className="px-4 py-3">
                    <div>
                      <div className="font-medium">
                        {user.display_name || user.email}
                      </div>
                      {user.display_name && (
                        <div className="text-xs text-muted-foreground">{user.email}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={statusColors[user.enrolment_status] ?? ''}>
                      {user.enrolment_status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <RoleCell user={user} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {user.last_login_at
                      ? new Date(user.last_login_at).toLocaleDateString()
                      : 'Never'}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// --- Helper component ---

function RoleCell({ user }: { user: User }) {
  const hasOwnership = user.ownership_roles.length > 0
  const hasRoles = user.role_names.length > 0

  if (!hasOwnership && !hasRoles) {
    return <span className="text-muted-foreground">None</span>
  }

  return (
    <div className="flex flex-col gap-0.5">
      {hasOwnership && (
        <>
          {user.ownership_roles.map((name) => (
            <span key={name} className="text-xs font-medium text-amber-700">
              {name}
            </span>
          ))}
          {hasRoles && (
            <div className="my-0.5 border-t border-border" />
          )}
        </>
      )}
      {hasRoles &&
        user.role_names.map((name) => (
          <span key={name} className="text-xs text-muted-foreground">
            {name}
          </span>
        ))}
    </div>
  )
}
